/**
 * FetchStatus holds status information about the status of a fetch request.
 */
export class FetchStatus {
  private readonly _statusCode: number;
  private readonly _statusValue: string;
  private static emptyFetchStatus = new FetchStatus(0, '');

  /**
   * Returns an empty FetchStatus object.
   */
  static get empty() {
    return FetchStatus.emptyFetchStatus;
  }

  /**
   * Creates a new FetchStatus object.
   * @param statusCode - The HTTP status code of the fetch request.
   * @param statusValue - The status value of the fetch request.
   */
  constructor(statusCode: number, statusValue: string) {
    this._statusCode = statusCode;
    this._statusValue = statusValue;
  }

  /**
   * Returns the status code of the fetch request.
   */
  get statusCode(): number {
    return this._statusCode;
  }

  /**
   * Returns the status value of the fetch request.
   */
  get statusValue(): string {
    return this._statusValue;
  }

  /**
   * Returns true if the status code is 404; false otherwise.
   */
  public isNotFound() {
    return this.statusCode === 404;
  }

  /**
   * Returns true if the status code is 5xx; false otherwise.
   */
  public isError() {
    return this.statusCode >= 500 && this.statusCode <= 599;
  }
}

/**
 * FetchResult holds the result of a fetch request as well as the status.
 */
export type FetchResult<T> = {
  /**
   * The optional result of the fetch request.
   */
  result?: T;
  /**
   * The status of the fetch request.
   */
  status: FetchStatus;
};

/**
 * Fetches a URL and returns the result as a {@link FetchResult}.
 * @param url - The URL to fetch.
 * @param headers - The headers to use for the fetch request.
 */
export async function fetchUrlAsObject<T>(
  url: string,
  headers?: any,
): Promise<FetchResult<T>> {
  const resp = await fetch(url, {
    headers: headers,
  });
  const statusInfo = new FetchStatus(resp.status, resp.statusText);
  if (resp.status === 200) {
    return { result: await resp.json(), status: statusInfo };
  }
  return { status: statusInfo };
}

/**
 * Fetches a URL and returns the result as a {@link FetchResult} with a result in text format.
 * @param url - The URL to fetch.
 * @param headers - The headers to use for the fetch request.
 */
export async function fetchUrlAsString(
  url: string,
  headers?: any,
): Promise<FetchResult<string>> {
  const resp = await fetch(url, {
    headers: headers,
  });
  const statusInfo = new FetchStatus(resp.status, resp.statusText);
  if (resp.status === 200) {
    return { result: await resp.text(), status: statusInfo };
  }
  return { status: statusInfo };
}
